// Factory HUD: a materials readout (always) + a build palette and control hints (build mode only).
// Self-contained DOM overlay in the qualionix palette (void / cyan / amber, mono uppercase).

import { BUILDINGS, PALETTE, CLIMATE_PALETTE, FLAVOR, ITEM_CODE, CODE_TO_KIND, BULLDOZE_CODE, unlockAt, rarityOf, type BuildingKind } from "./defs";

const CSS = `
.fx-mats {
  position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 40;
  font-family: "Space Mono", ui-monospace, monospace; text-align: center; pointer-events: none;
  text-shadow: 0 0 12px rgba(0,0,0,0.8);
}
.fx-mats .big { font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700;
  font-size: 26px; letter-spacing: 0.04em; color: #ffce8a; }
.fx-mats .big b { color: #fff; }
.fx-mats .sub { font-size: 10px; letter-spacing: 0.24em; color: #5c7f88; text-transform: uppercase; margin-top: 2px; }

.fx-invpanel {
  /* STACKING ORDER IS THE WHOLE POINT: backdrop (this) → 3D canvas → slot chrome + text. Painting the backdrop
     on the grid itself buried every model under it, and a text node inside the grid can never be lifted above a
     sibling canvas — so the panel has to be a separate ancestor and the canvas has to live between the two. */
  /* LEFT COLUMN: taller than it is wide, on the side the dev panel used to occupy. A bottom-centered strip ate
     the width of the screen and buried the battlefield; a column leaves the middle clear. */
  position: fixed; left: 14px; top: 50%; transform: translateY(-50%); z-index: 40;
  display: flex; flex-direction: column; gap: 6px;
  background: linear-gradient(180deg, rgba(6,14,20,0.94), rgba(4,9,13,0.97));
  border: 1px solid rgba(63,208,230,0.30); border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.5), 0 10px 34px rgba(0,0,0,0.55), inset 0 1px 0 rgba(120,220,255,0.07);
  backdrop-filter: blur(3px);
}
.fx-invpanel.hidden { display: none; }
/* DETAIL PANE: the selected item, shown much larger, with its flavor. Its own preview canvas sits behind it. */
.fx-detail { position: relative; z-index: 3; border-top: 1px solid rgba(63,208,230,0.22); padding-top: 6px; }
.fx-detail.hidden { display: none; }
.fx-detail .well { position: relative; height: 132px; }
.fx-detail.mystery .well::after {   /* no model, just the question — the shape itself would be a spoiler */
  content: "?"; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 56px; color: #3c4c5c;
}
.fx-detail.mystery .dn { color: #6b7d8c; }
.fx-detail .dn {
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 14px;
  letter-spacing: 0.08em; text-transform: uppercase; color: #ffce8a; text-align: center; margin-top: 2px;
}
.fx-detail .dmeta { font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase; color: #5c7f88; text-align: center; }
.fx-detail .dflav {
  font-size: 10.5px; line-height: 1.5; color: #9fb4c4; margin-top: 5px; padding: 0 4px 2px;
  max-width: 322px; text-align: left;
}
.fx-bar {
  position: relative;
  /* P6: a REAL tiled grid — every building keeps a permanent cell so its position never moves, grayed when you
     own none. A reflowing bar meant slots jumped around as stock changed and muscle memory never formed. */
  display: grid; grid-template-columns: repeat(3, 104px); gap: 5px; justify-content: center;
  /* A CAPPED, SCROLLING frame: showing all ~30 slots at once ate the bottom of the screen, and every one of
     them was a live 3D viewport. Two-and-a-bit rows visible, the rest a scroll away — and the preview layer
     culls whatever is off-screen, so cost tracks what you can actually see, not what you own. */
  max-height: 46vh; overflow-y: auto; overscroll-behavior: contain;
  padding: 8px; scrollbar-width: thin; scrollbar-color: #3fd0e6 transparent;
  background: transparent;   /* the backdrop lives on .fx-invpanel, BEHIND the 3D canvas — see below */
  position: relative; z-index: 2;
  font-family: "Space Mono", ui-monospace, monospace;
}
.fx-slot .q {           /* owned count — TOP right, out of the name's way */
  position: absolute; right: 4px; top: 2px;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 15px; color: #fff;
  text-shadow: 0 0 6px #000, 0 0 3px #000;
}
.fx-slot .pv {          /* preview well — the shared 3D canvas shows through here on OWNED slots */
  height: 84px; display: flex; align-items: center; justify-content: center;   /* the model gets the cell */
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 34px; color: #46586b;
}
.fx-slot.empty { opacity: 0.5; }         /* owned none — kept in place, but it is a MYSTERY, not a ghost */
.fx-slot.empty .n { visibility: hidden; }  /* the name is the reveal: you learn it by pulling one */
.fx-slot.empty .q { color: #7f8c99; }
.fx-inv3d {   /* ONE canvas for the whole grid; each slot is a scissored viewport on it. Between backdrop and chrome.
     ABSOLUTE, not fixed: the panel is transform:translateX(-50%), which makes it the containing block for fixed
     children too — so viewport coordinates would offset the canvas FROM the panel and fling it off to one side. */
  position: absolute; z-index: 1; pointer-events: none;
}
.fx-pack {
  position: relative; align-self: stretch; text-align: center; z-index: 3;
  cursor: pointer; pointer-events: auto; padding: 6px 16px;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 12px;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: #ffce8a; border: 1px solid #f2762e; background: rgba(242,118,46,0.14);
  transition: background 0.15s ease;
}
.fx-pack:hover { background: rgba(242,118,46,0.3); }
.fx-pack.poor { opacity: 0.45; cursor: default; }
/* pack + dev-stock share a row: pack takes the slack, the cheat stays a fixed tab beside it */
.fx-buyrow { position: relative; z-index: 3; display: flex; gap: 5px; align-items: stretch; }
.fx-buyrow .fx-pack { flex: 1 1 auto; }
.fx-stock {
  flex: 0 0 auto; display: flex; align-items: center; cursor: pointer; pointer-events: auto; padding: 6px 10px;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 12px;
  letter-spacing: 0.10em; color: #8cff7a; border: 1px dashed rgba(140,255,122,0.6);
  background: rgba(140,255,122,0.10); transition: background 0.15s ease;
}
.fx-stock:hover { background: rgba(140,255,122,0.24); }
.fx-stock.hidden { display: none; }
/* OBLITERATOR control strip — only present when the player owns one. Red-keyed so it reads as its panel. */
.fx-oblrow { position: relative; z-index: 3; display: flex; gap: 5px; align-items: stretch; }
.fx-oblrow.hidden { display: none; }
.fx-obl {
  flex: 1 1 auto; text-align: center; cursor: pointer; pointer-events: auto; padding: 6px 8px;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 11px;
  letter-spacing: 0.10em; text-transform: uppercase;
  color: #ff8a8a; border: 1px solid rgba(255,25,25,0.55); background: rgba(255,25,25,0.12);
  transition: background 0.15s ease, color 0.15s ease;
}
.fx-obl:hover { background: rgba(255,25,25,0.28); }
.fx-obl.wide { flex: 1.4 1 auto; }
/* ORE CANNON aiming menu — opens on clicking a placed cannon. Orange-keyed to the building's own accent. */
.fx-cannon {
  position: relative; z-index: 3; pointer-events: auto; display: flex; flex-direction: column; gap: 6px;
  padding: 8px; border: 1px solid rgba(255,138,58,0.55); background: rgba(255,138,58,0.10);
}
.fx-cannon.hidden { display: none; }
.fx-cannon .hd {
  display: flex; justify-content: space-between; align-items: center;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 11px;
  letter-spacing: 0.12em; text-transform: uppercase; color: #ffb37a;
}
.fx-cannon .x { cursor: pointer; padding: 0 4px; color: #ff8a3a; }
.fx-cannon .x:hover { color: #fff; }
.fx-cannon .row { display: flex; gap: 6px; align-items: center; }
.fx-cannon label {
  flex: 0 0 46px; font-family: "Space Mono", monospace; font-size: 10px;
  letter-spacing: 0.06em; color: #c69a7a; text-transform: uppercase;
}
.fx-cannon input[type=number] {
  flex: 1 1 auto; min-width: 0; background: rgba(0,0,0,0.35); color: #ffd9b8;
  border: 1px solid rgba(255,138,58,0.4); font-family: "Space Mono", monospace; font-size: 11px; padding: 3px 5px;
}
.fx-cannon input[type=range] { flex: 1 1 auto; min-width: 0; accent-color: #ff8a3a; }
.fx-cannon .val { flex: 0 0 42px; text-align: right; font-family: "Space Mono", monospace; font-size: 10px; color: #ffd9b8; }
.fx-cannon .warn { font-family: "Space Mono", monospace; font-size: 10px; line-height: 1.5; color: #7f97a6; }
.fx-cannon .warn.bad { color: #ff6a4a; }
.fx-obl.on { color: #59d5ff; border-color: rgba(89,213,230,0.7); background: rgba(89,213,230,0.18); }
.fx-obl.off { opacity: 0.4; cursor: default; }
.fx-bar.hidden { display: none; }
.fx-bar::-webkit-scrollbar { width: 6px; }
.fx-bar::-webkit-scrollbar-thumb { background: rgba(63,208,230,0.45); }
.fx-bar::-webkit-scrollbar-track { background: transparent; }
.fx-slot {
  position: relative; min-width: 0; padding: 4px 3px 5px; cursor: pointer; text-align: center;
  border: 1px solid rgba(63,208,230,0.22); background: rgba(5,12,16,0.15); color: #cfeef5;  /* near-clear: the model shows through */
  transition: border-color 0.12s ease, background 0.12s ease;
}
.fx-slot:hover { border-color: rgba(63,208,230,0.6); }
.fx-slot.sel { border-color: #f2762e; background: rgba(242,118,46,0.14); }
.fx-slot .k {           /* two-letter selection code, top-left opposite the count */
  position: absolute; left: 4px; top: 2px;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700;
  font-size: 9px; letter-spacing: 0.14em; line-height: 1.1;
  color: #6b7d8c;       /* GRAY on purpose: legible enough to memorize, quiet enough not to compete with the
                           model, the name or the count. It is a reference, not a call to action. */
}
.fx-slot.empty .k { visibility: hidden; }  /* an unowned slot reveals nothing — not its name, not its code */
.fx-search {
  flex: 0 0 auto; position: relative; z-index: 3; margin: 0 0 1px;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 11px; letter-spacing: 0.08em;
  padding: 5px 8px; width: 100%; box-sizing: border-box;
  color: #cfeef5; background: rgba(5,12,16,0.55); border: 1px solid rgba(63,208,230,0.30); outline: none;
}
.fx-search::placeholder { color: #4d6875; letter-spacing: 0.10em; }
.fx-search:focus { border-color: rgba(63,208,230,0.75); background: rgba(5,12,16,0.8); }
/* ECHO — reads the command back in words. This is what makes "AA342" learnable instead of cryptic. */
.fx-echo {
  flex: 0 0 auto; position: relative; z-index: 3; margin: 0 0 3px; padding: 3px 8px;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 10px; letter-spacing: 0.06em;
  color: #9fb4c4; background: rgba(5,12,16,0.5); border-left: 2px solid #3fd0e6;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fx-echo.hidden { display: none; }
.fx-echo.bad { color: #ff8a8a; border-left-color: #ff2a2a; }
.fx-echo.kill { color: #ffb27a; border-left-color: #f2762e; }
.fx-echo b { color: #ffce8a; font-weight: 700; }
.fx-slot.sel .k { color: #b98a63; }  /* still muted when selected */
.fx-slot .n { font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 10px;
  letter-spacing: 0.03em; text-transform: uppercase; margin: 0; line-height: 1.15;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fx-slot .c { font-size: 8px; color: #ffce8a; }
.fx-slot .c.free { color: #5c7f88; }
/* P5 rarity: a colored left edge (starter = none) */
.fx-slot.r-common { border-left: 3px solid #8cff7a; }
.fx-slot.r-rare   { border-left: 3px solid #7cf0ff; }
.fx-slot.r-epic   { border-left: 3px solid #c78bff; }
/* P5 locked: grayed until its launch milestone; shows the threshold instead of the cost */
.fx-slot.locked { opacity: 0.42; filter: grayscale(0.7); cursor: default; }
.fx-slot.locked .c { color: #6b7683; }
.fx-toast.pack { color: #8cff7a; font-size: 13px; letter-spacing: 0.08em; }

.fx-hint {
  position: fixed; bottom: 92px; left: 50%; transform: translateX(-50%); z-index: 40;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 11px; letter-spacing: 0.06em;
  color: #5c7f88; pointer-events: none; white-space: nowrap;
}
.fx-hint.hidden { display: none; }
.fx-hint b { color: #3fd0e6; }
.fx-toast {
  position: fixed; bottom: 130px; left: 50%; transform: translateX(-50%); z-index: 41;
  font-family: "Space Mono", monospace; font-size: 12px; color: #ff7a5a; letter-spacing: 0.06em;
  opacity: 0; transition: opacity 0.2s ease; pointer-events: none;
}
.fx-toast.on { opacity: 1; }

.fx-power {
  position: fixed; top: 56px; left: 50%; transform: translateX(-50%); z-index: 40;
  font-family: "Space Mono", monospace; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: #5ad0ff; pointer-events: none; text-shadow: 0 0 10px rgba(0,0,0,0.85);
}
.fx-power.hidden { display: none; }
.fx-power .warn { color: #ff7a5a; }

/* scanner readout (top-left): rolling median + peak worth scanned, only while a scanner exists */
.fx-scan {
  position: fixed; top: 16px; left: 18px; z-index: 40; line-height: 1.5;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 11px; letter-spacing: 0.06em;
  color: #e79bff; pointer-events: none; text-shadow: 0 0 10px rgba(0,0,0,0.85);
}
.fx-scan.hidden { display: none; }
.fx-scan .cap { font-size: 9px; letter-spacing: 0.22em; color: #a05fb0; text-transform: uppercase; }
.fx-scan b { color: #fff; font-family: "Chakra Petch", "Space Mono", monospace; }

/* tile inspector: follows the cursor, names the tile + what's stacked on it (a comms aid — the grid is
   impossible to eyeball in a convoluted base, so this is how a coordinate gets reported precisely) */
.fx-tile {
  position: fixed; z-index: 60; pointer-events: none; padding: 5px 8px;
  background: rgba(8,10,16,0.82); border: 1px solid rgba(120,200,255,0.28); border-radius: 3px;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 11px; line-height: 1.45;
  color: #cfe6ff; text-shadow: 0 0 8px rgba(0,0,0,0.9); white-space: pre;
}
.fx-tile.hidden { display: none; }

/* DEV ore inspector: pinned readout for the one ore the developer clicked (see Factory.latchOre) */
.fx-orepin {
  position: fixed; right: 14px; bottom: 14px; z-index: 61; pointer-events: none; padding: 7px 10px;
  background: rgba(8,10,16,0.9); border: 1px solid rgba(255,180,90,0.45); border-radius: 3px;
  font-family: "Space Mono", ui-monospace, monospace; font-size: 11px; line-height: 1.5;
  color: #cfe6ff; text-shadow: 0 0 8px rgba(0,0,0,0.9); white-space: pre; max-width: 340px;
}
.fx-orepin.hidden { display: none; }
.fx-orepin .xy { color: #ffd479; font-weight: 700; letter-spacing: 0.06em; }
.fx-orepin .fl { color: #7f94ad; }
.fx-orepin .on { color: #8affc0; }
.fx-orepin .bad { color: #ff9a7a; }
.fx-tile .xy { color: #fff; font-weight: 700; letter-spacing: 0.08em; }
.fx-tile .fl { color: #7f94ad; }
.fx-tile .on { color: #ffd479; }
.fx-tile .em { color: #8affc0; }

.fx-buildkey {
  position: fixed; bottom: 22px; left: 50%; transform: translateX(-50%); z-index: 40;
  font-family: "Chakra Petch", "Space Mono", monospace; font-weight: 700; font-size: 13px;
  letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; pointer-events: auto;
  padding: 9px 20px; color: #f7a86a; border: 1px solid #f2762e; background: rgba(242,118,46,0.14);
  box-shadow: 0 0 22px rgba(242,118,46,0.25); transition: background 0.15s ease, color 0.15s ease;
}
.fx-buildkey:hover { background: rgba(242,118,46,0.28); color: #ffd9b8; }
.fx-buildkey.hidden { display: none; }
.fx-buildkey b { color: #3fd0e6; }

.fx-floor {
  position: fixed; right: 20px; top: 50%; transform: translateY(-50%); z-index: 40;
  display: flex; flex-direction: column-reverse; gap: 5px; align-items: center;
  font-family: "Space Mono", monospace; pointer-events: none;
}
.fx-floor.hidden { display: none; }
.fx-floor .cap { font-size: 9px; letter-spacing: 0.18em; color: #5c7f88; text-transform: uppercase; margin-bottom: 3px; }
.fx-floor .flr {
  width: 34px; height: 22px; display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(63,208,230,0.3); background: rgba(5,12,16,0.82); color: #5c7f88;
  font-size: 12px; font-weight: 700;
}
.fx-floor .flr.on { border-color: #f2762e; background: rgba(242,118,46,0.18); color: #ffce8a; }
.fx-floor .keys { font-size: 9px; letter-spacing: 0.1em; color: #3fd0e6; margin-top: 3px; }

/* share-code controls (build mode only): export the layout to the clipboard / import one from a pasted code */
.fx-io {
  position: fixed; left: 20px; bottom: 20px; z-index: 40;
  display: flex; flex-direction: column; gap: 6px; font-family: "Space Mono", ui-monospace, monospace;
}
.fx-io.hidden { display: none; }
.fx-io button {
  cursor: pointer; padding: 7px 12px; font: inherit; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: #9fd8e6; background: rgba(5,12,16,0.82); border: 1px solid rgba(63,208,230,0.28);
  transition: border-color 0.12s ease, color 0.12s ease;
}
.fx-io button:hover { border-color: rgba(63,208,230,0.6); color: #dff4fa; }
.fx-io .cap { font-size: 8px; letter-spacing: 0.18em; color: #5c7f88; text-transform: uppercase; }
`;

const BULLDOZE = "bulldoze";
type Sel = BuildingKind | typeof BULLDOZE;

/** Compact worth formatting for the scanner readout (1.2 · 340 · 5.1k · 2.3M). */
function fmtVal(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "k";
  if (v >= 100) return String(Math.round(v));
  return v.toFixed(1);
}

export class FactoryHud {
  private mats: HTMLDivElement;
  private matsVal: HTMLElement;
  private launched: HTMLElement;
  private oreCount: HTMLElement;
  private bar: HTMLDivElement;
  private hint: HTMLDivElement;
  private toast: HTMLDivElement;
  private toastT = 0;
  private power: HTMLDivElement;
  private scan: HTMLDivElement;
  private tile: HTMLDivElement;
  private panel!: HTMLDivElement;
  private detail!: HTMLDivElement;
  /** The detail pane's big preview well — the factory hangs a second, larger 3D view on it. */
  get detailWell(): HTMLElement { return this.detail.querySelector(".well") as HTMLElement; }
  onDetail: (kind: BuildingKind | null) => void = () => {};
  private packBtn: HTMLDivElement | null = null;
  private stockBtn: HTMLDivElement | null = null;
  onPack: () => void = () => {};
  onStockAll: () => void = () => {};
  onObliterate: () => void = () => {};   // wipe THIS factory's ore
  onAimCannon: (tileX: number, tileZ: number, height: number) => void = () => {};
  onAimCannonManual: (yawDeg: number, pitchDeg: number, powerPct: number) => void = () => {};
  onCloseCannon: () => void = () => {};
  onTogglePause: () => void = () => {};  // pause/resume THIS factory's ore sim
  onStepOre: () => void = () => {};      // advance one ore frame while paused
  private search!: HTMLInputElement;
  private echo!: HTMLDivElement;
  private keepCode = false;   // set while re-seeding the box after a place, so the code does not re-fire select
  onPlaceAt: (sel: Sel, gx: number, gz: number, dir: number) => void = () => {};
  onProbe: (sel: Sel, gx: number, gz: number, dir: number) => { ok: boolean; text: string } = () => ({ ok: false, text: "" });
  private oblRow: HTMLDivElement | null = null;
  private cannonBox: HTMLDivElement | null = null;
  private cannonTitle!: HTMLSpanElement;
  private cannonX!: HTMLInputElement;
  private cannonZ!: HTMLInputElement;
  private cannonH!: HTMLInputElement;
  private cannonYaw!: HTMLInputElement;
  private cannonPitch!: HTMLInputElement;
  private cannonPow!: HTMLInputElement;
  private cannonYawVal!: HTMLSpanElement;
  private cannonPitchVal!: HTMLSpanElement;
  private cannonPowVal!: HTMLSpanElement;
  private cannonWarn!: HTMLDivElement;
  private oblPause: HTMLDivElement | null = null;
  private oblStep: HTMLDivElement | null = null;   // DEV: fill the inventory so a build can be tested without grinding for it
  /** Slots that currently hold something — the 3D preview layer draws one spinning model per entry. */
  private ownedSlots: { key: string; el: HTMLElement }[] = [];
  onOwnedChanged: (slots: { key: string; el: HTMLElement }[]) => void = () => {};
  private orePin: HTMLDivElement;
  private buildKey: HTMLDivElement;
  private floor: HTMLDivElement;
  private floorMax = 1;
  private io: HTMLDivElement;
  private slots = new Map<Sel, HTMLDivElement>();
  onSelect: (sel: Sel) => void = () => {};
  onBuild: () => void = () => {}; // clicking the idle "[B] BUILD" prompt
  onExport: () => void = () => {}; // copy a shareable code of the whole layout
  onImport: () => void = () => {}; // rebuild the layout from a pasted code

  constructor(container: HTMLElement) {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    this.mats = document.createElement("div");
    this.mats.className = "fx-mats";
    this.mats.innerHTML = `<div class="big">◆ <b>0</b></div><div class="sub">orbital materials · <span class="lz">0</span> launched · <span class="orez">0</span> ore</div>`;
    container.appendChild(this.mats);
    this.matsVal = this.mats.querySelector("b")!;
    this.launched = this.mats.querySelector(".lz")!;
    this.oreCount = this.mats.querySelector(".orez")!;

    this.bar = document.createElement("div");
    this.bar.className = "fx-bar";   // visibility is the PANEL's job now (it wraps this)
    for (const kind of PALETTE) {
      const d = BUILDINGS[kind];
      this.bar.appendChild(this.slot(kind, ITEM_CODE[kind] ?? "", d.name));
    }
    this.bar.appendChild(this.slot(BULLDOZE, "X", "Bulldoze"));
    this.packBtn = document.createElement("div");
    this.packBtn.className = "fx-pack";
    this.packBtn.textContent = "◆ … · open pack";
    this.packBtn.addEventListener("click", () => this.onPack());
    this.panel = document.createElement("div");
    this.panel.className = "fx-invpanel hidden";
    // SEARCH — matches the two-letter code or the literal name. An exact code selects immediately on the
    // second letter; that is the hotkey replacement, so it must land without needing Enter.
    this.search = document.createElement("input");
    this.search.className = "fx-search";
    this.search.type = "text";
    this.search.spellcheck = false;
    this.search.placeholder = "search  ·  name or 2-letter code";
    // The game listens for keys on window (rotate, bulldoze, build...). Without this, typing "r" in the box
    // would rotate the ghost. Stop the events at the input rather than teaching every handler about focus.
    for (const ev of ["keydown", "keyup", "keypress"]) this.search.addEventListener(ev, (e) => e.stopPropagation());
    this.search.addEventListener("input", () => this.applySearch());
    this.search.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { this.search.value = ""; this.applySearch(); this.search.blur(); }
      else if (e.key === "Enter") this.commitCommand();
    });
    this.panel.appendChild(this.search);
    // "/" focuses the box from anywhere in build mode — the command line is only ever one key away, and the
    // slash itself is swallowed so it never lands in the field.
    window.addEventListener("keydown", (e) => {
      if (e.key !== "/" || this.panel.classList.contains("hidden")) return;
      if (document.activeElement === this.search) return;
      e.preventDefault(); e.stopPropagation();
      this.search.focus(); this.search.select();
    });
    this.echo = document.createElement("div");
    this.echo.className = "fx-echo hidden";
    this.panel.appendChild(this.echo);
    this.panel.appendChild(this.bar);           // canvas is inserted between the two by InventoryPreview
    // IN THE PANEL, BELOW THE GRID — but NOT inside .fx-bar, which scrolls; this must stay reachable at any
    // scroll position. It used to be appended to the outer HUD container, which is what lost it when the
    // inventory moved into this fixed left column: `.fx-pack` is `align-self: stretch`, and align-self only
    // means anything inside a flex parent. Out there it had none, so it laid out in the container's own flow
    // instead of the panel and effectively vanished.
    // pack button + the DEV stock-all cheat share one row, so the cheat sits beside it without stealing height
    const buyRow = document.createElement("div");
    buyRow.className = "fx-buyrow";
    buyRow.appendChild(this.packBtn);
    this.stockBtn = document.createElement("div");
    this.stockBtn.className = "fx-stock hidden";   // DEV ONLY — shown by setDev()
    this.stockBtn.textContent = "99×";
    this.stockBtn.title = "DEV: give 99 of every building";
    this.stockBtn.addEventListener("click", () => this.onStockAll());
    buyRow.appendChild(this.stockBtn);
    this.panel.appendChild(buyRow);
    // OBLITERATOR strip. Hidden until the player actually owns one — these are its abilities, not free tools.
    this.oblRow = document.createElement("div");
    this.oblRow.className = "fx-oblrow hidden";
    const mk = (label: string, cls: string, fn: () => void) => {
      const el = document.createElement("div");
      el.className = "fx-obl" + (cls ? " " + cls : "");
      el.textContent = label;
      el.addEventListener("click", fn);
      this.oblRow!.appendChild(el);
      return el;
    };
    mk("✹ clear ore", "wide", () => this.onObliterate());
    this.oblPause = mk("pause", "", () => this.onTogglePause());
    this.oblStep = mk("step", "off", () => this.onStepOre());
    this.panel.appendChild(this.oblRow);

    // ORE CANNON aiming menu. Two ways in, and only one of them is authoritative at a time: type a TILE + height
    // and the gun solves for it, or drive bearing/elevation directly and the target readout becomes derived.
    // Touching a slider hands ownership to the sliders; typing a tile takes it back.
    this.cannonBox = document.createElement("div");
    this.cannonBox.className = "fx-cannon hidden";
    const hd = document.createElement("div");
    hd.className = "hd";
    this.cannonTitle = document.createElement("span");
    this.cannonTitle.textContent = "ore cannon";
    const x = document.createElement("span");
    x.className = "x"; x.textContent = "✕";
    x.addEventListener("click", () => this.onCloseCannon());
    hd.appendChild(this.cannonTitle); hd.appendChild(x);
    this.cannonBox.appendChild(hd);

    const numRow = (label: string, step: number): HTMLInputElement => {
      const r = document.createElement("div"); r.className = "row";
      const l = document.createElement("label"); l.textContent = label;
      const i = document.createElement("input");
      i.type = "number"; i.step = String(step);
      i.addEventListener("change", () => this.pushCannonTarget());
      // keystrokes here must never reach the build hotkeys (R rotates, X bulldozes, [ ] change floor)
      i.addEventListener("keydown", (e) => { e.stopPropagation(); if (e.key === "Enter") i.blur(); });
      r.appendChild(l); r.appendChild(i); this.cannonBox!.appendChild(r);
      return i;
    };
    this.cannonX = numRow("tile x", 0.5);
    this.cannonZ = numRow("tile z", 0.5);
    this.cannonH = numRow("floor", 1);

    const slider = (label: string, min: number, max: number): [HTMLInputElement, HTMLSpanElement] => {
      const r = document.createElement("div"); r.className = "row";
      const l = document.createElement("label"); l.textContent = label;
      const i = document.createElement("input");
      i.type = "range"; i.min = String(min); i.max = String(max); i.step = "1";
      const v = document.createElement("span"); v.className = "val";
      i.addEventListener("input", () => this.pushCannonManual());
      r.appendChild(l); r.appendChild(i); r.appendChild(v); this.cannonBox!.appendChild(r);
      return [i, v];
    };
    [this.cannonYaw, this.cannonYawVal] = slider("bearing", -180, 180);
    [this.cannonPitch, this.cannonPitchVal] = slider("elev", 0, 85);
    // Power is a READOUT while a target is set (the solve picks it) and a CONTROL once you grab a slider.
    [this.cannonPow, this.cannonPowVal] = slider("power", 0, 100);

    this.cannonWarn = document.createElement("div");
    this.cannonWarn.className = "warn";
    this.cannonBox.appendChild(this.cannonWarn);
    this.panel.appendChild(this.cannonBox);
    this.detail = document.createElement("div");
    this.detail.className = "fx-detail hidden";
    this.detail.innerHTML = `<div class="well"></div><div class="dn"></div><div class="dmeta"></div><div class="dflav"></div>`;
    this.panel.appendChild(this.detail);
    container.appendChild(this.panel);
    for (const kind of CLIMATE_PALETTE) { const el = this.slots.get(kind); if (el) el.style.display = "none"; } // shown only on hot/cold worlds

    this.power = document.createElement("div");
    this.power.className = "fx-power hidden";
    container.appendChild(this.power);

    this.scan = document.createElement("div");
    this.scan.className = "fx-scan hidden";
    container.appendChild(this.scan);

    this.tile = document.createElement("div");
    this.tile.className = "fx-tile hidden";
    container.appendChild(this.tile);

    this.orePin = document.createElement("div");
    this.orePin.className = "fx-orepin hidden";
    container.appendChild(this.orePin);

    // the idle prompt so the player knows how to open the build palette (hidden while building / deployed)
    this.buildKey = document.createElement("div");
    this.buildKey.className = "fx-buildkey hidden";
    this.buildKey.innerHTML = `◆ Build your factory · <b>[B]</b>`;
    this.buildKey.addEventListener("click", () => this.onBuild());
    container.appendChild(this.buildKey);

    this.hint = document.createElement("div");
    this.hint.className = "fx-hint hidden";
    this.hint.innerHTML = `<b>pick</b> a part · <b>R</b> rotate · <b>click</b> place · <b>[ ]</b> floor · <b>X</b> bulldoze · <b>B</b> exit`;
    container.appendChild(this.hint);

    // floor selector readout (build mode only): a vertical stack of floor pills, active highlighted
    this.floor = document.createElement("div");
    this.floor.className = "fx-floor hidden";
    container.appendChild(this.floor);

    // share-code controls (build mode only)
    this.io = document.createElement("div");
    this.io.className = "fx-io hidden";
    const cap = document.createElement("div"); cap.className = "cap"; cap.textContent = "layout";
    const exp = document.createElement("button"); exp.textContent = "⎘ Export"; exp.title = "Copy a shareable code of this factory";
    exp.addEventListener("click", () => this.onExport());
    const imp = document.createElement("button"); imp.textContent = "⎀ Import"; imp.title = "Rebuild from a pasted factory code";
    imp.addEventListener("click", () => this.onImport());
    this.io.append(cap, exp, imp);
    container.appendChild(this.io);

    this.toast = document.createElement("div");
    this.toast.className = "fx-toast";
    container.appendChild(this.toast);
  }

  private slot(sel: Sel, key: string, name: string): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "fx-slot";
    if (sel !== BULLDOZE) el.classList.add(`r-${rarityOf(sel)}`); // P5 rarity edge
    // P6: buildings are OWNED, so a slot shows the count you hold, not a price. Bulldoze keeps its own label.
    const foot = sel === BULLDOZE ? `<div class="c free">refund</div>` : `<div class="q">0</div>`;
    const well = sel === BULLDOZE ? "" : `<div class="pv">?</div>`;   // "?" until you own one; then the model spins here
    el.innerHTML = `${key ? `<div class="k">${key}</div>` : ""}${well}<div class="n">${name}</div>${foot}`;
    el.dataset.code = key;
    el.dataset.name = name.toLowerCase();
    if (sel !== BULLDOZE) el.classList.add("empty");
    el.addEventListener("click", () => {
      // A "?" slot stays a "?". Selecting it would fill the detail pane with the name, the model and the
      // flavor — handing over the exact surprise the mystery slot exists to withhold. You cannot place one
      // you do not own either, so there is nothing lost by refusing the selection.
      if (sel !== BULLDOZE && el.classList.contains("empty")) { this.showMystery(); return; }
      this.onSelect(sel);
    });
    this.slots.set(sel, el);
    return el;
  }

  setMaterials(m: number, launched: number): void {
    this.matsVal.textContent = String(Math.floor(m));
    this.launched.textContent = String(Math.floor(launched));
  }

  /** Live count of active ore riding this factory's belts (parts excluded) — the real number behind the physics/clog budget. */
  setOreCount(n: number): void {
    this.oreCount.textContent = String(n);
  }

  /** DEV: pinned live readout for a single latched ore, so the developer can confirm they clicked the one they
   *  meant and watch what is holding it still. */
  setOrePin(html: string | null): void {
    if (!html) { this.orePin.classList.add("hidden"); return; }
    this.orePin.innerHTML = html;
    this.orePin.classList.remove("hidden");
  }

  /** Tile inspector that trails the cursor: the hovered tile's coordinates + the whole floor stack on it. */
  setTileTip(px: number, py: number, html: string | null): void {
    if (!html) { this.tile.classList.add("hidden"); return; }
    this.tile.innerHTML = html;
    this.tile.classList.remove("hidden");
    const w = this.tile.offsetWidth || 120, h = this.tile.offsetHeight || 60;
    const x = Math.min(px + 16, window.innerWidth - w - 8);          // flip to the cursor's left near the right edge
    const y = Math.min(Math.max(py + 16, 8), window.innerHeight - h - 8);
    this.tile.style.left = `${x}px`;
    this.tile.style.top = `${y}px`;
  }

  /** Filter the grid, and fire an instant selection on an exact two-letter code.
   *  Codes can never collide with the first two letters of a name (see ITEM_CODE), so "co" is unambiguously
   *  the start of "Conveyor" and never a code — which is what makes typing a name safe. */
  /** Parse `[code][x][z][rot]` — one character per field. Unambiguous because a two-letter code can never be
   *  the start of a name (see ITEM_CODE), so "letters then digits" needs no lookahead. Returns null for a
   *  plain name search. */
  private parseCommand(raw: string): { sel: Sel; gx?: number; gz?: number; dir?: number } | null {
    const m = /^([a-z]{2})([0-9]?)([0-9]?)([0-3]?)$/i.exec(raw.trim());
    if (!m) return null;
    const code = m[1].toUpperCase();
    const sel: Sel | undefined = code === BULLDOZE_CODE ? BULLDOZE : CODE_TO_KIND[code];
    if (!sel) return null;
    const out: { sel: Sel; gx?: number; gz?: number; dir?: number } = { sel };
    if (m[2] !== "") out.gx = +m[2];
    if (m[3] !== "") out.gz = +m[3];
    if (m[4] !== "") out.dir = +m[4];
    return out;
  }

  /** Enter: commit the typed command. Placement spends inventory and mutates the layout, so unlike SELECTION
   *  (which is free and reversible, and fires on the second letter) it waits for a deliberate keypress.
   *  On success the CODE is kept and only the coordinates cleared, so `34↵ 35↵ 36↵` walks a row. */
  private commitCommand(): void {
    const cmd = this.parseCommand(this.search.value);
    if (!cmd || cmd.gx === undefined || cmd.gz === undefined) return;
    const probe = this.onProbe(cmd.sel, cmd.gx, cmd.gz, cmd.dir ?? 0);
    if (!probe.ok) return;                       // the echo is already showing why
    this.onPlaceAt(cmd.sel, cmd.gx, cmd.gz, cmd.dir ?? 0);
    const code = this.search.value.trim().slice(0, 2);
    // Keep the tool, drop the coords, so `34↵ 35↵ 36↵` walks a row. The flag matters: a bare two-letter code
    // normally fires INSTANT SELECT, which clears the box — that would wipe the tool we just chose to keep.
    this.keepCode = true;
    this.search.value = code;
    this.applySearch();
    this.keepCode = false;
  }

  private applySearch(): void {
    const raw = this.search.value.trim();
    const q = raw.toLowerCase();
    const code = raw.replace(/[^a-z0-9]/gi, "").replace(/[0-9]/g, "").toUpperCase();
    // LIVE ECHO — read the command back in words so the digits never have to be memorized, and show why an
    // invalid one will not commit BEFORE Enter is pressed.
    const cmd = this.parseCommand(raw);
    if (cmd && cmd.gx !== undefined && cmd.gz !== undefined) {
      const p = this.onProbe(cmd.sel, cmd.gx, cmd.gz, cmd.dir ?? 0);
      this.echo.className = "fx-echo" + (p.ok ? (cmd.sel === BULLDOZE ? " kill" : "") : " bad");
      this.echo.innerHTML = p.text;
      this.search.value !== "" ? this.echo.classList.remove("hidden") : this.echo.classList.add("hidden");
    } else {
      this.echo.classList.add("hidden");
    }
    // exact code → select NOW (only if owned; an unowned slot is a "?" whose code is hidden anyway)
    if (code.length === 2 && CODE_TO_KIND[code] && !/[0-9]/.test(raw) && !this.keepCode) {
      const kind = CODE_TO_KIND[code];
      const el = this.slots.get(kind);
      if (el && !el.classList.contains("empty")) {
        this.search.value = "";
        this.applySearch();
        this.search.blur();
        this.onSelect(kind);
        return;
      }
    }
    for (const [sel, el] of this.slots) {
      if (sel === BULLDOZE) continue;
      const hidden = el.classList.contains("empty");
      // an unowned slot has no discoverable name or code, so it only survives an EMPTY query
      const hit = !q || (!hidden && ((el.dataset.name ?? "").includes(q) || (el.dataset.code ?? "").toLowerCase().startsWith(q)));
      el.style.display = hit ? "" : "none";
    }
  }

  /** DEV-only affordances. Off in a normal session, so the cheat can never be clicked by a player. */
  setDev(on: boolean): void {
    if (this.stockBtn) this.stockBtn.classList.toggle("hidden", !on);
  }

  /** Show the Obliterator strip only when one is placed. */
  setHasObliterator(on: boolean): void {
    if (this.oblRow) this.oblRow.classList.toggle("hidden", !on);
  }

  /** Draw (or hide, on null) the cannon aiming menu. Values are only written into fields the player is not
   *  currently editing — otherwise re-rendering after every keystroke fights their typing. */
  setCannon(s: {
    gx: number; gz: number; tileX: number; tileZ: number; floor: number; maxFloor: number;
    yawDeg: number; pitchDeg: number; powerPct: number; manual: boolean; reachable: boolean;
    why: "ok" | "close" | "far"; rangeTiles: number; minTiles: number;
  } | null): void {
    if (!this.cannonBox) return;
    this.cannonBox.classList.toggle("hidden", !s);
    if (!s) return;
    const a = document.activeElement;
    const set = (el: HTMLInputElement, v: number): void => { if (el !== a) el.value = String(Math.round(v * 10) / 10); };
    this.cannonTitle.textContent = `ore cannon · ${s.gx},${s.gz}`;
    set(this.cannonX, s.tileX); set(this.cannonZ, s.tileZ); set(this.cannonH, s.floor);
    set(this.cannonYaw, s.yawDeg); set(this.cannonPitch, s.pitchDeg); set(this.cannonPow, s.powerPct);
    this.cannonYawVal.textContent = `${Math.round(s.yawDeg)}°`;
    this.cannonPitchVal.textContent = `${Math.round(s.pitchDeg)}°`;
    this.cannonPowVal.textContent = `${Math.round(s.powerPct)}%`;
    // Say plainly when a target cannot be reached. A cannon quietly dumping ore into a gap is the exact failure
    // this factory is worst at showing, so it gets the loud treatment rather than a silent clamp.
    this.cannonWarn.classList.toggle("bad", !s.reachable && !s.manual);
    // TOO CLOSE and TOO FAR are different failures and the fix is different, so they get different words.
    // "Out of range" for a target one tile away would send the player hunting for more power.
    this.cannonWarn.textContent = s.manual
      ? "aimed by hand — the target readout is where this shot lands"
      : s.reachable
        ? `in range at ${Math.round(s.powerPct)}% power · high arc, so ore stay where they land`
        : s.why === "close"
          ? `TOO CLOSE (nearest ~${s.minTiles.toFixed(1)} tiles even at the gentlest lob) — ore will land long`
          : `TOO FAR (max ~${s.rangeTiles.toFixed(1)} tiles at full power, less uphill) — ore will fall short and be lost`;
  }

  private pushCannonTarget(): void {
    this.onAimCannon(Number(this.cannonX.value) || 0, Number(this.cannonZ.value) || 0, Number(this.cannonH.value) || 0);
  }

  private pushCannonManual(): void {
    this.onAimCannonManual(Number(this.cannonYaw.value) || 0, Number(this.cannonPitch.value) || 0,
                           Number(this.cannonPow.value) || 0);
  }

  /** Reflect the ore-sim pause state: the pause button lights, and STEP only becomes live while paused. */
  setOrePaused(on: boolean): void {
    if (this.oblPause) { this.oblPause.textContent = on ? "resume" : "pause"; this.oblPause.classList.toggle("on", on); }
    if (this.oblStep) this.oblStep.classList.toggle("off", !on);
  }

  setBuildMode(on: boolean): void {
    // the pack button lives INSIDE the panel now, so the panel hiding hides it too — one owner of visibility,
    // not two (a stale second toggle is what left `.fx-bar` stuck hidden once already)
    if (!on && this.search) { this.search.value = ""; this.applySearch(); }
    this.panel.classList.toggle("hidden", !on);
    this.hint.classList.toggle("hidden", !on);
    this.floor.classList.toggle("hidden", !(on && this.floorMax > 1));
    this.io.classList.toggle("hidden", !on);
    if (on) this.buildKey.classList.add("hidden"); // the prompt is redundant once the palette is open
  }

  /** Update the floor selector: `active` is the current floor (0-based), `max` the number of floors. */
  setFloor(active: number, max: number): void {
    this.floorMax = max;
    let html = `<div class="cap">floor</div>`;
    for (let f = 0; f < max; f++) html += `<div class="flr${f === active ? " on" : ""}">${f + 1}</div>`;
    html += `<div class="keys">[ ]</div>`;
    this.floor.innerHTML = html;
    if (max > 1 && !this.bar.classList.contains("hidden")) this.floor.classList.remove("hidden");
  }

  /** Show the "[B] BUILD" prompt when idle in the free camera (hidden while building or deployed). */
  setIdlePrompt(show: boolean): void {
    this.buildKey.classList.toggle("hidden", !show);
  }

  /** Show the generator/cooler slots + the power readout only when the world needs climate control. */
  setClimateMode(on: boolean): void {
    for (const kind of CLIMATE_PALETTE) { const el = this.slots.get(kind); if (el) el.style.display = on ? "" : "none"; }
    this.power.classList.toggle("hidden", !on);
  }

  setPower(need: boolean, supply: number, demand: number, choking: number, kind: "none" | "hot" | "cold"): void {
    if (!need) { this.power.classList.add("hidden"); return; }
    const label = kind === "cold" ? "heat" : "cool";
    const status = choking > 0 ? `<span class="warn">⚠ ${choking} choking</span>` : "stable";
    this.power.innerHTML = `⚡ ${supply}/${demand} pwr · ${label} · ${status}`;
    this.power.classList.remove("hidden");
  }

  /** Scanner readout: the rolling-median + peak worth scanned. Shown only while a scanner exists. */
  setScanStats(has: boolean, median: number, peak: number): void {
    this.scan.classList.toggle("hidden", !has);
    if (!has) return;
    this.scan.innerHTML = `<div class="cap">◈ scanner</div>median ◆<b>${fmtVal(median)}</b> · peak ◆<b>${fmtVal(peak)}</b>`;
  }

  /** P6: paint the owned counts, gray the empties, and price the next pack. Slots NEVER move — an empty one
   *  stays exactly where it was so you learn the grid once. */
  setInventory(inv: Map<BuildingKind, number>, packPrice: number, materials: number): void {
    for (const [sel, el] of this.slots) {
      if (sel === BULLDOZE) continue;
      const n = inv.get(sel as BuildingKind) ?? 0;
      const q = el.querySelector(".q") as HTMLElement | null;
      if (q) q.textContent = String(n);
      el.classList.toggle("empty", n <= 0);
      // OWNED → the shared 3D canvas draws the spinning model in this cell, so the well must not also show "?".
      const pv = el.querySelector(".pv") as HTMLElement | null;
      if (pv) pv.textContent = n > 0 ? "" : "?";
    }
    this.ownedSlots = [...this.slots.entries()]
      .filter(([k]) => k !== BULLDOZE && (inv.get(k as BuildingKind) ?? 0) > 0)
      .map(([k, el]) => ({ key: k as string, el: el.querySelector(".pv") as HTMLElement }));
    this.onOwnedChanged(this.ownedSlots);
    if (this.packBtn) {
      const poor = materials < packPrice;
      this.packBtn.textContent = `◆ ${packPrice} · open pack`;
      this.packBtn.classList.toggle("poor", poor);
    }
  }

  /** The grid element — the 3D preview layer parents its canvas here and reads slot rects from it. */
  get barEl(): HTMLElement { return this.bar; }

  /** Detail pane for something you have never pulled: acknowledges the click, reveals nothing. */
  private showMystery(): void {
    this.detail.classList.remove("hidden");
    this.detail.classList.add("mystery");
    (this.detail.querySelector(".dn") as HTMLElement).textContent = "UNIDENTIFIED";
    (this.detail.querySelector(".dmeta") as HTMLElement).textContent = "not yet acquired";
    (this.detail.querySelector(".dflav") as HTMLElement).textContent =
      "Nothing in the manifest matches this slot. Open packs until something does.";
    this.onDetail(null);   // clear the big 3D view — the silhouette alone would give it away
  }

  setSelected(sel: Sel): void {
    for (const [k, el] of this.slots) el.classList.toggle("sel", k === sel);
    // Second section: the selection, shown large, with what it is FOR in the game's own voice.
    const kind = sel === BULLDOZE ? null : (sel as BuildingKind);
    this.detail.classList.toggle("hidden", !kind);
    this.detail.classList.remove("mystery");
    if (kind) {
      const d = BUILDINGS[kind];
      (this.detail.querySelector(".dn") as HTMLElement).textContent = d.name;
      const at = unlockAt(kind);
      (this.detail.querySelector(".dmeta") as HTMLElement).textContent =
        `${rarityOf(kind)}${at ? ` · unlocks at ${at}` : ""}${d.directional ? " · directional" : ""}`;
      (this.detail.querySelector(".dflav") as HTMLElement).textContent = FLAVOR[kind] ?? "";
    }
    this.onDetail(kind);
  }

  flash(msg: string): void {
    this.toast.textContent = msg;
    this.toast.classList.remove("pack");
    this.toast.classList.add("on");
    this.toastT = 1.4;
  }

  /** P5: gray out (and label with their launch threshold) the buildings not yet unlocked at `launched`. */
  refreshLocks(launched: number): void {
    for (const [sel, el] of this.slots) {
      if (sel === BULLDOZE) continue;
      const at = unlockAt(sel);
      const locked = launched < at;
      el.classList.toggle("locked", locked);
      const c = el.querySelector(".c") as HTMLElement | null;
      if (c) c.innerHTML = locked ? `🔒 ${at}` : `◆ ${BUILDINGS[sel].cost}`;
    }
  }

  /** P5: celebratory "pack opened" toast when a launch milestone unlocks new buildings. */
  pack(names: string[]): void {
    this.toast.innerHTML = `⬦ PACK UNLOCKED · ${names.join(" · ")}`;
    this.toast.classList.add("on", "pack");
    this.toastT = 3.2;
  }

  update(dt: number): void {
    if (this.toastT > 0) {
      this.toastT -= dt;
      if (this.toastT <= 0) this.toast.classList.remove("on", "pack");
    }
  }
}

export { BULLDOZE };
export type { Sel };
